import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import { Green, Gray } from "@components/Dot";
import { getDevices, AdminDevice } from "@services/adminService";
import { ROUTES } from "@stores/AccountStore";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import Sparkline from "@components/Sparkline";
import ActivityGrid from "@components/ActivityGrid";
import useIsMobile from "@src/hooks/useIsMobile";
import {
  isDeviceOnline,
  formatLastOnline,
  formatLastOnlineExact,
} from "@src/utils/deviceOnline";

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInputStyled = styled(Input)`
  height: 40px;
  flex: 1;
  max-width: 600px;
  padding: 0 12px;
  box-sizing: border-box;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: none;
  }
`;

const SearchButton = styled(Button)`
  width: auto;
  min-width: 80px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const DeviceList = styled.div`
  display: flex;
  flex-direction: column;
`;

const DeviceCard = styled(Link)`
  padding: 16px;
  border-bottom: 1px solid #f0f1f3;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  gap: 12px;
  &:hover {
    background: #fafafa;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const DeviceLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const DeviceRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const EsnLink = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: bold;
  color: #4a90d9;
`;

const DeviceInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 6px;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 6px;
`;

const InfoLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #899093;
`;

const InfoValue = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #494c5b;
`;

const Arrow = styled.span`
  color: #899093;
  font-size: 18px;
`;

const DeviceName = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #494c5b;
`;

const AliasLabel = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: bold;
  color: #494c5b;
  margin-top: 2px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding-bottom: 32px;
`;

const PageButton = styled(Button)`
  width: auto;
  min-width: 80px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EsnRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const LastOnline = styled.span<{ online: boolean }>`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  padding: 2px 8px;
  color: ${({ online }) => (online ? "#1f7a3d" : "#899093")};
  background: ${({ online }) => (online ? "#e6f4ea" : "#f0f1f3")};
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterSelect = styled.select`
  height: 40px;
  padding: 0 12px;
  box-sizing: border-box;
  background: #f8f8f8;
  border: 1px solid transparent;
  border-radius: 4px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  cursor: pointer;
  outline: none;
  transition: 0.4s;
  :hover,
  :focus {
    border: 1px solid #899093;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

// The two charts sit between the device facts and the name/arrow. They are the
// widest thing in the row, so they are the first to go when it gets narrow —
// the ESN and the link must stay reachable.
const ChartSlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;

  @media (max-width: 1150px) {
    display: none;
  }
`;

const ChartLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #899093;
`;

const Badge = styled.span`
  display: inline-block;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
  background: #f0f1f3;
  border-radius: 12px;
  padding: 4px 12px;
  margin-bottom: 16px;
`;

const PAGE_SIZE = 50;

// Chart window. Ten days is the default because it is the shortest window that
// still shows a working pattern; the whole range comes from the cached daily
// table, so a longer window costs the same one query.
const DEFAULT_CHART_DAYS = 10;
const CHART_DAY_OPTIONS = [5, 10, 30, 90];

type SortKey = "lastConnectedAt" | "createdAt";
type SortOrder = "asc" | "desc";

// One select drives both query params; the backend takes them separately.
const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "lastConnectedAt:desc", label: "Last online first" },
  { value: "lastConnectedAt:asc", label: "Longest offline first" },
  { value: "createdAt:desc", label: "Newest added" },
  { value: "createdAt:asc", label: "Oldest added" },
];

/**
 * The same two visuals the client devices screen shows, at the window the admin
 * picked: the harvest activity grid (working seconds per day) and the sensor
 * trend with fault days shaded.
 *
 * Both read the one `health.days` array that came down with the list — the grid
 * takes `workSeconds`, the trend takes `avgValue` and `severity`. No request of
 * its own, and nothing here touches `tracks`.
 *
 * A short window would make the GitHub calendar shape a meaningless two-column
 * block, so the grid runs as a single strip until the window is long enough for
 * whole weeks to read.
 */
const DeviceCharts: React.FC<{ device: AdminDevice; days: number }> = ({
  device,
  days,
}) => {
  const cells = device.health?.days ?? [];

  const activity: Record<string, number> = {};
  for (const d of cells) if (d.workSeconds) activity[d.day] = d.workSeconds;

  const workedDays = Object.keys(activity).length;
  const workedHours = Math.round(
    Object.values(activity).reduce((a, s) => a + s, 0) / 3600
  );
  const faultDays = cells.filter((d) => d.severity !== "ok").length;

  const gridTitle =
    workedDays === 0
      ? `No harvesting recorded in the last ${days} days`
      : `${workedHours} h harvested across ${workedDays} of the last ${days} days`;
  const sparkTitle =
    cells.length === 0
      ? "No sensor history cached for this device yet"
      : faultDays > 0
      ? `${faultDays} of ${cells.length} cached days had a sensor fault`
      : `No sensor faults in ${cells.length} cached days`;

  return (
    <ChartSlot>
      <div title={gridTitle}>
        <ActivityGrid
          days={activity}
          span={days}
          layout={days <= 31 ? "strip" : "calendar"}
          cell={days <= 31 ? 8 : 4}
          gap={days <= 31 ? 2 : 1}
        />
      </div>
      <div title={sparkTitle}>
        <Sparkline
          days={cells}
          channel={device.health?.channel ?? "value"}
          width={days <= 31 ? days * 10 - 2 : 140}
          height={26}
          muted={!isDeviceOnline(device.lastConnectedAt)}
        />
      </div>
      <ChartLabel>
        {workedDays > 0 ? `${workedHours} h · ` : ""}
        {faultDays > 0 ? `${faultDays} fault day${faultDays > 1 ? "s" : ""}` : "no faults"}
      </ChartLabel>
    </ChartSlot>
  );
};

const AdminDevicesTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [devicesList, setDevicesList] = useState<AdminDevice[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState<"" | "online" | "offline">("");
  const [attached, setAttached] = useState<"" | "with" | "without">("");
  const [sort, setSort] = useState<string>("lastConnectedAt:desc");
  // Deliberately not persisted — a reload goes back to ten days.
  const [chartDays, setChartDays] = useState(DEFAULT_CHART_DAYS);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    const [sortKey, sortOrder] = sort.split(":") as [SortKey, SortOrder];
    try {
      const data = await getDevices({
        search: searchQuery || undefined,
        status: status || undefined,
        attached: attached || undefined,
        sort: sortKey,
        order: sortOrder,
        // Charts ride along on the list request — the backend reads the cached
        // daily table in the same round trip, so the rows cost no extra call.
        healthDays: chartDays,
        limit: PAGE_SIZE,
        offset,
      });
      setDevicesList(data.devices);
      setTotal(data.total);
    } catch (e) {
      console.error("Failed to fetch devices:", e);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, status, attached, sort, chartDays, offset]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleSearch = () => {
    setOffset(0);
    setSearchQuery(search);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div>
      <SearchRow>
        <SearchInputStyled
          placeholder="Search by ESN, alias, IMEI, or ICCID..."
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
          onKeyDown={handleKeyDown}
        />
        <SearchButton kind="outline" onClick={handleSearch}>
          Search
        </SearchButton>
      </SearchRow>

      <FilterRow>
        <FilterSelect
          value={status}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setOffset(0);
            setStatus(e.target.value as "" | "online" | "offline");
          }}
        >
          <option value="">All statuses</option>
          <option value="online">Online only</option>
          <option value="offline">Offline only</option>
        </FilterSelect>

        <FilterSelect
          value={attached}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setOffset(0);
            setAttached(e.target.value as "" | "with" | "without");
          }}
        >
          <option value="">All devices</option>
          <option value="with">Attached to a user</option>
          <option value="without">Not attached</option>
        </FilterSelect>

        <FilterSelect
          value={sort}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setOffset(0);
            setSort(e.target.value);
          }}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          value={chartDays}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setChartDays(Number(e.target.value))
          }
          title="How many days the activity grid and sensor trend cover"
        >
          {CHART_DAY_OPTIONS.map((d) => (
            <option key={d} value={d}>
              Charts: last {d} days
            </option>
          ))}
        </FilterSelect>
      </FilterRow>

      <Badge>{total} devices</Badge>

      {loading ? (
        <LoadingWrap>
          <Spinner />
        </LoadingWrap>
      ) : (
        <DeviceList>
          {devicesList.map((device) => (
            <DeviceCard key={device.esn} to={`${ROUTES.ADMIN}/devices/${device.esn}`}>
              <DeviceLeft>
                <EsnRow>
                  {isDeviceOnline(device.lastConnectedAt) ? <Green /> : <Gray />}
                  <EsnLink>{device.esn}</EsnLink>
                  <LastOnline
                    online={isDeviceOnline(device.lastConnectedAt)}
                    title={formatLastOnlineExact(device.lastConnectedAt)}
                  >
                    {formatLastOnline(device.lastConnectedAt)}
                  </LastOnline>
                </EsnRow>
                {device.alias && <AliasLabel>{device.alias}</AliasLabel>}
                <DeviceInfo>
                  {device.iccid && (
                    <InfoItem>
                      <InfoLabel>ICCID</InfoLabel>
                      <InfoValue>{device.iccid}</InfoValue>
                    </InfoItem>
                  )}
                  {device.imei && (
                    <InfoItem>
                      <InfoLabel>IMEI</InfoLabel>
                      <InfoValue>{device.imei}</InfoValue>
                    </InfoItem>
                  )}
                  {device.attachedUser && (
                    <InfoItem>
                      <InfoLabel>User ID</InfoLabel>
                      <InfoValue style={{ color: "#4a90d9" }}>
                        {device.attachedUser.id.substring(0, 20)}...
                      </InfoValue>
                    </InfoItem>
                  )}
                  {device.bootloaderVersion && (
                    <InfoItem>
                      <InfoLabel>Bootloader</InfoLabel>
                      <InfoValue>{device.bootloaderVersion}</InfoValue>
                    </InfoItem>
                  )}
                  {device.strategyVersion && (
                    <InfoItem>
                      <InfoLabel>Strategy</InfoLabel>
                      <InfoValue>{device.strategyVersion}</InfoValue>
                    </InfoItem>
                  )}
                </DeviceInfo>
              </DeviceLeft>
              <DeviceRight>
                {!isMobile && <DeviceCharts device={device} days={chartDays} />}
                {(device.attachmentName || device.alias) && (
                  <DeviceName>
                    {device.attachmentName || device.alias}
                  </DeviceName>
                )}
                <Arrow>&rarr;</Arrow>
              </DeviceRight>
            </DeviceCard>
          ))}
        </DeviceList>
      )}

      {!loading && total > PAGE_SIZE && (
        <Pagination>
          <PageButton
            kind="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </PageButton>
          <Text size={13} color="#899093">
            {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
          </Text>
          <PageButton
            kind="outline"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </PageButton>
        </Pagination>
      )}
    </div>
  );
};

export default AdminDevicesTab;
